import { type Dispatch, type FC, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@redux/store';
import cn from 'classnames';
import { AnimatePresence, motion, transform } from 'framer-motion';

import { useLazyGetLeagueInfoByIdQuery } from '@/api/queries/league';
import { toShortNumFormat } from '@/common/lib/toShortNumFormat';
import { type LeagueType } from '@/common/types/league';
import { ResourceTitle } from '@/components/ResourceTitle/ResourceTitle';
import {
  StringMain,
  StringSmall,
  StringXSmall,
} from '@/components/Typography/Typography';
import { ArrowNav } from '@/components/ui/ArrowNav/ArrowNav';
import { MotionPicture } from '@/components/ui/Picture/Picture';

import { leagueClassNames, leagueNames, leagueRankImages } from '../config';

import css from './styles.module.scss';

interface LeagueNavigationProps {
  activeLeague?: LeagueType;
  activeLeagueRank?: number;
  setActiveLeagueRank: Dispatch<SetStateAction<number | undefined>>;
}

const LeagueNavigation: FC<LeagueNavigationProps> = ({
  setActiveLeagueRank,
  activeLeagueRank,
  activeLeague,
}) => {
  const { t } = useTranslation();
  const [getLeaguyById] = useLazyGetLeagueInfoByIdQuery();
  const empireValue = useAppSelector(
    (state) => state.user.extendedInfo?.totalEmpireValue,
  );

  const name =
    activeLeague?.name ||
    (activeLeagueRank ? leagueNames[activeLeagueRank] : undefined);

  const changeLegue = (leagueId?: number) => {
    return (): void => {
      if (leagueId !== undefined) {
        getLeaguyById({ leagueId });
      }
    };
  };

  const changeSlide = (leagueId?: number) => {
    return (): void => {
      if (leagueId !== undefined) {
        setActiveLeagueRank(leagueId);
      }
    };
  };

  const percentage = activeLeague
    ? transform(
        [activeLeague.progress.from, activeLeague.progress.to],
        [0, 100],
      )
    : undefined;

  return (
    name && (
      <div className="w-full px-4 pb-5 relative overflow-hidden">
        <div className={css.overlay}></div>
        <ResourceTitle className="mb-6 isolate">{t(name)}</ResourceTitle>
        <div className="w-full h-24 flex items-center justify-between relative mb-7">
          <ArrowNav
            arrowPosition="left"
            onClickCapture={changeLegue(activeLeague?.prevLeagueId)}
            onClick={changeSlide(activeLeague?.prevLeagueId)}
            className={cn(
              activeLeague?.prevLeagueId === undefined && 'opacity-50',
            )}
          />
          <div className="w-24 relative h-full">
            <AnimatePresence>
              <MotionPicture
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: 'easeOut' }}
                key={`rank-picture-${name}`}
                className="absolute top-0 left-0 w-24 h-28"
                img={leagueRankImages[name].png}
                webp={leagueRankImages[name].webp}
                alt="league rank"
              />
            </AnimatePresence>
          </div>
          <AnimatePresence>
            <motion.div
              key={`effect-${name}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
              className={cn(
                css.effect,
                css[`effect--${leagueClassNames[name]}`],
              )}
            ></motion.div>
          </AnimatePresence>
          <ArrowNav
            arrowPosition="right"
            onClickCapture={changeLegue(activeLeague?.nextLeagueId)}
            onClick={changeSlide(activeLeague?.nextLeagueId)}
            className={cn(
              activeLeague?.nextLeagueId === undefined && 'opacity-50',
            )}
          />
        </div>
        <div className="flex flex-col gap-2 isolate">
          <div className="flex items-center justify-between relative">
            <StringSmall weight="bold" className="!text-white/60">
              {`${t('from')} ${toShortNumFormat(activeLeague?.progress?.from || 0)}`}
            </StringSmall>
            {empireValue !== undefined && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[134px] flex gap-1 flex-col items-center">
                <StringXSmall
                  weight="medium"
                  className="!text-white/60 text-nowrap"
                >
                  {t('Empire Capitalization')}
                </StringXSmall>
                <StringMain weight="bold">
                  {toShortNumFormat(empireValue || 0)}
                </StringMain>
              </div>
            )}
            <StringSmall weight="bold" className="!text-white/60">
              {`${t('to')} ${toShortNumFormat(activeLeague?.progress?.to || 0)}`}
            </StringSmall>
          </div>
          <div className="w-full h-2 rounded-lg bg-white/20">
            <div
              style={{
                width: `${percentage ? percentage(empireValue || 0) : 0}%`,
              }}
              className={cn(
                css.progress,
                css[`progress--${leagueClassNames[name]}`],
              )}
            ></div>
          </div>
        </div>
      </div>
    )
  );
};

export { LeagueNavigation };
